
import { MEDIA_TYPE } from '../base/media/constants';
import { FakeParticipant } from '../base/mtApi/constants';
import { PARTICIPANT_ROLE } from '../base/participants/constants';
import {
    getAllParticipantAndSharedFrames,
    getLocalParticipant,
    getParticipantById,
    getParticipants,
    getVirtualScreenshareParticipantOwnerId,
    isLocalParticipantModerator,
    isParticipantModerator
} from '../base/participants/functions';
import { toState } from '../base/redux';
import { isInBreakoutRoom } from '../breakout-rooms/functions.any';

/**
 * Reads the fake-participant kind of a participant. The property has been
 * spelled both ways over the lifetime of this codebase (and the mtApi reducer
 * still writes the legacy one), so both are consulted.
 *
 * @param {Object} participant - The participant entity.
 * @private
 * @returns {?string}
 */
function _getFakeParticipantType(participant) {
    return participant?.fakeParticipant ?? participant?.isFakeParticipant;
}

/**
 * Returns true when Focus Mode is currently enabled for the meeting.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {boolean}
 */
export function isFocusModeEnabled(stateful: Object | Function) {
    return Boolean(toState(stateful)['features/focus-mode']?.enabled);
}

/**
 * Returns true when the local user keeps full visibility while Focus Mode is on.
 * Hosts and co-hosts (both are the moderator role) supervise the meeting, so
 * they are never restricted.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {boolean}
 */
export function isLocalExemptFromFocusMode(stateful: Object | Function) {
    return isLocalParticipantModerator(stateful);
}

/**
 * Returns true when the local client has to hide the video of everyone who is
 * not a host. This is the single predicate every other part of the feature is
 * built on: Focus Mode is on and the local user is not a host.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {boolean}
 */
export function shouldRestrictFocusModeView(stateful: Object | Function) {
    return isFocusModeEnabled(stateful) && !isLocalExemptFromFocusMode(stateful);
}

/**
 * Whether the current meeting has Focus Mode enabled in its backend settings.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {boolean}
 */
export function isFocusModeAllowedForMeeting(stateful: Object | Function) {
    const state = toState(stateful);
    const focusModeValue
        = state['features/base/jwt']?.confSettings?.FOCUS_MODE
            ?? state['features/base/jwt']?.meetingDetails?.settings?.FOCUS_MODE;

    return focusModeValue === 1;
}

/**
 * Whether the Focus Mode toggle should be offered to the local user: they are a
 * host, they are in the main room, and the deployment has not disabled it.
 *
 * NOTE: {@code isInBreakoutRoom} is taken from breakout-rooms/functions.any
 * rather than the platform barrel - the web barrel pulls in
 * screen-share/actions, which reaches base/tracks and back into this module.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {boolean}
 */
export function isFocusModeToggleVisible(stateful: Object | Function) {
    const state = toState(stateful);
    const { conference } = state['features/base/conference'] || {};
    const { disableFocusMode } = state['features/base/config'] || {};

    return Boolean(conference)
        && isFocusModeAllowedForMeeting(state)
        && isLocalParticipantModerator(state)
        && !disableFocusMode
        && !isInBreakoutRoom(state);
}

/**
 * Resolves the real participant behind a screenshare tile. The tile is a fake
 * participant whose id is the screenshare source name, and it carries no role of
 * its own, so the decision has to be made on its owner.
 *
 * @param {Object} state - The redux state.
 * @param {Object} participant - A (possibly fake) participant.
 * @private
 * @returns {?Object}
 */
function _getScreenshareOwner(state, participant) {
    if (!participant) {
        return undefined;
    }

    const ownerId = getVirtualScreenshareParticipantOwnerId(participant.id)
        || participant.ownerId
        || participant.latestOwnerId;

    return ownerId ? getParticipantById(state, ownerId) : undefined;
}

/**
 * Returns true when the given participant (or tile) may be rendered for the
 * local user under Focus Mode.
 *
 * Allowed: yourself, your own screenshare, every host, and anything a host is
 * presenting. Everything else is another attendee and stays hidden - which is
 * also what makes a participant's screenshare private to the hosts, since the
 * other attendees simply never render it.
 *
 * @param {Object} state - The redux state.
 * @param {Object} participant - The participant entity.
 * @returns {boolean}
 */
export function isParticipantAllowedInFocusMode(state: Object, participant: Object) {
    if (!participant) {
        return false;
    }

    if (participant.local) {
        return true;
    }

    if (isParticipantModerator(participant)) {
        return true;
    }

    const fakeType = _getFakeParticipantType(participant);

    if (fakeType === FakeParticipant.LocalScreenShare || fakeType === FakeParticipant.RemoteScreenShare) {
        const local = getLocalParticipant(state);
        const owner = _getScreenshareOwner(state, participant);

        if (owner?.local || (owner?.id && owner.id === local?.id)) {
            return true;
        }

        return isParticipantModerator(owner);
    }

    if (fakeType === FakeParticipant.SharedVideo || participant.name === 'YouTube') {
        const ownerId = state['features/shared-video']?.ownerId;

        return isParticipantModerator(ownerId ? getParticipantById(state, ownerId) : undefined);
    }

    // Whiteboard / shared document tiles are collaborative content rather than a
    // peer's camera, so they stay visible to everybody.
    if (fakeType === FakeParticipant.Whiteboard) {
        return true;
    }

    return false;
}

/**
 * Returns true when a participant id - which may also be a screenshare source
 * name that has no participant entry of its own yet - may be shown to the local
 * user. Anything outside Focus Mode is allowed.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @param {?string} participantId - Participant or screenshare source id.
 * @returns {boolean}
 */
export function isParticipantIdAllowedInFocusMode(stateful: Object | Function, participantId: ?string) {
    const state = toState(stateful);

    if (!participantId) {
        return false;
    }

    if (!shouldRestrictFocusModeView(state)) {
        return true;
    }

    const participant = getParticipantById(state, participantId);

    if (participant) {
        return isParticipantAllowedInFocusMode(state, participant);
    }

    // No participant entry: this is a bare screenshare source name, so fall back
    // to the endpoint that owns it.
    const ownerId = getVirtualScreenshareParticipantOwnerId(participantId);

    if (ownerId && ownerId !== participantId) {
        return isParticipantAllowedInFocusMode(state, getParticipantById(state, ownerId));
    }

    return false;
}

/**
 * Memoization for {@link getFocusModeVisibleParticipants}. The selector runs on
 * every render of the filmstrip and the tile view, so it must not hand back a
 * fresh array when nothing it depends on has changed.
 */
const _visibleParticipants = {
    participants: undefined,
    sharedVideoOwnerId: undefined,
    result: undefined
};

/**
 * The participants the local user is allowed to see. Hosts - and everybody when
 * Focus Mode is off - get the untouched list, by reference, so that consumers
 * relying on identity comparisons keep working exactly as before.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {Array<Object>}
 */
export function getFocusModeVisibleParticipants(stateful: Object | Function) {
    const state = toState(stateful);
    const participants = state['features/base/participants'] || [];

    if (!shouldRestrictFocusModeView(state)) {
        return participants;
    }

    const sharedVideoOwnerId = state['features/shared-video']?.ownerId;

    if (_visibleParticipants.participants === participants
            && _visibleParticipants.sharedVideoOwnerId === sharedVideoOwnerId) {
        return _visibleParticipants.result;
    }

    _visibleParticipants.participants = participants;
    _visibleParticipants.sharedVideoOwnerId = sharedVideoOwnerId;
    _visibleParticipants.result = participants.filter(p => isParticipantAllowedInFocusMode(state, p));

    return _visibleParticipants.result;
}

/**
 * The remote, non-fake participants the local user is allowed to see. Used
 * wherever thumbnails are indexed by position in the remote list.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {Array<Object>}
 */
export function getFocusModeVisibleRemoteParticipants(stateful: Object | Function) {
    return getFocusModeVisibleParticipants(stateful)
        .filter(p => !p?.local && !_getFakeParticipantType(p));
}

/**
 * The tile ids the local user is allowed to see, in the same shape (participants
 * plus shared-content frames) that {@link getAllParticipantAndSharedFrames}
 * returns, so it can be dropped in wherever tile counts are computed.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {Array<string>}
 */
export function getFocusModeVisibleParticipantIds(stateful: Object | Function) {
    const state = toState(stateful);
    const allIds = getAllParticipantAndSharedFrames(state);

    if (!shouldRestrictFocusModeView(state)) {
        return allIds;
    }

    const allowed = new Set(getFocusModeVisibleParticipants(state).map(p => p.id));

    // Ids with no participant entry are the shared-content frames (whiteboard,
    // livepad); they are not peer video and stay in the list.
    return allIds.filter(id => allowed.has(id) || !getParticipantById(state, id));
}

/**
 * The real (non-fake) participants the local user may see listed in the
 * participants pane. Keeping the roster in step with the tiles avoids a pane
 * that names thirty people next to a stage that shows two.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @returns {Array<Object>}
 */
export function getFocusModeVisibleRoster(stateful: Object | Function) {
    const state = toState(stateful);
    const participants = getParticipants(state);

    if (!shouldRestrictFocusModeView(state)) {
        return participants;
    }

    return participants.filter(p => isParticipantAllowedInFocusMode(state, p));
}

/**
 * The participants of another (breakout) room that the local user may see
 * listed. The breakout-rooms roster is broadcast to everybody by prosody, so
 * without this a restricted guest sitting in a breakout room could read off the
 * whole main-room attendee list from the participants pane.
 *
 * @param {Object|Function} stateful - The redux state or {@code getState}.
 * @param {Object} room - A breakout-rooms room entry.
 * @returns {Array<Object>}
 */
export function getFocusModeVisibleRoomParticipants(stateful: Object | Function, room: Object) {
    const participants = Object.values(room?.participants || {});

    if (!shouldRestrictFocusModeView(stateful)) {
        return participants;
    }

    return participants.filter(p => p?.role === PARTICIPANT_ROLE.MODERATOR);
}

/**
 * The video source names a restricted local user should keep receiving from the
 * bridge. Everything else is dropped before it reaches this client, so a peer's
 * camera or screenshare never arrives here at all.
 *
 * @param {Object} state - The redux state.
 * @returns {Array<string>}
 */
export function getFocusModeAllowedSourceNames(state: Object) {
    const tracks = state['features/base/tracks'] || [];
    const local = getLocalParticipant(state);
    const sourceNames = new Set();

    tracks.forEach(track => {
        if (track.local || track.mediaType !== MEDIA_TYPE.VIDEO) {
            return;
        }

        const { participantId } = track;

        if (!participantId || participantId === local?.id) {
            return;
        }

        if (!isParticipantIdAllowedInFocusMode(state, participantId)) {
            return;
        }

        const sourceName = track.mhTrack?.getSourceName?.();

        if (sourceName) {
            sourceNames.add(sourceName);
        }
    });

    return [ ...sourceNames ];
}
